import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Grid, List, ListItem, ListItemText, Typography, Avatar, Stack, ToggleButton, ToggleButtonGroup, Drawer, Chip, Button, Divider, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getTopCreators, getTopWinningFilms, getTopGenres, getWeeklyWinners, getPollsByUserSeries, me, getWinnersSummary, getMovieByImdb, getMovieByTitle, buildApiUrl } from '../services/api';

function GenreHeatmap({ data, maxItems = 20 }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No genre data available</Typography>
      </Box>
    );
  }

  // Sort by count and take top items
  const sortedData = [...data]
    .filter(item => item && item.genre && item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, maxItems);

  if (sortedData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No genre data available</Typography>
      </Box>
    );
  }

  const maxCount = Math.max(...sortedData.map(item => item.count));
  const minCount = Math.min(...sortedData.map(item => item.count));

  // Calculate grid dimensions
  const itemsPerRow = Math.ceil(Math.sqrt(sortedData.length));

  return (
    <Box sx={{ p: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
          gap: 0.5,
          maxWidth: 300,
          mx: 'auto'
        }}
      >
        {sortedData.map((item, index) => {
          // Calculate color intensity based on count
          const intensity = maxCount > minCount 
            ? (item.count - minCount) / (maxCount - minCount)
            : 1;
          
          // Use a blue color scale
          const colorIntensity = Math.max(0.1, intensity);
          const backgroundColor = `rgba(25, 118, 210, ${colorIntensity})`;
          
          return (
            <Tooltip
              key={item.genre}
              title={`${item.genre}: ${item.count} wins`}
              arrow
            >
              <Box
                sx={{
                  aspectRatio: '1',
                  backgroundColor,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 2
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: intensity > 0.5 ? 'white' : 'text.primary',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    lineHeight: 1,
                    px: 0.5
                  }}
                >
                  {item.genre.length > 8 ? item.genre.substring(0, 8) + '...' : item.genre}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Less
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[0.2, 0.4, 0.6, 0.8, 1.0].map((intensity) => (
            <Box
              key={intensity}
              sx={{
                width: 12,
                height: 12,
                backgroundColor: `rgba(25, 118, 210, ${intensity})`,
                borderRadius: 0.5
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          More
        </Typography>
      </Box>
    </Box>
  );
}

function LineChart({ points, width = 360, height = 160 }) {
  const padding = 24;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  if (!points.length) return <Box sx={{ height }} />;
  const maxVal = Math.max(...points.map(p => p.value), 1);
  const stepX = innerW / Math.max(points.length - 1, 1);
  const pathD = points.map((p, i) => {
    const x = padding + i * stepX;
    const y = padding + innerH - (p.value / maxVal) * innerH;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height}>
      <path d={pathD} fill="none" stroke="#1976d2" strokeWidth="2" />
    </svg>
  );
}

const Metrics = () => {
  const [topCreators, setTopCreators] = useState([]);
  const [topFilms, setTopFilms] = useState([]);
  const [genres, setGenres] = useState({ breakdown: [], total: 0 });
  const [weekly, setWeekly] = useState([]);
  const [series, setSeries] = useState([]);
  const [who, setWho] = useState('all');
  const [user, setUser] = useState(null);
  const [granularity, setGranularity] = useState('day');
  const [winnerSummary, setWinnerSummary] = useState({ winners: [] });
  const [weeklyDenied, setWeeklyDenied] = useState(false);
  const [personalDenied, setPersonalDenied] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null); // { title, year, poster, imdbID }
  const [omdbFull, setOmdbFull] = useState({ _for: null, data: null });

  useEffect(() => {
    (async () => {
      try {
        const u = await me().catch(() => null);
        setUser(u?.user || null);
      } catch (_) {}
      getTopCreators().then(d => setTopCreators(d.topCreators || [])).catch(() => setTopCreators([]));
      // Use minRatings=1 to be inclusive on smaller datasets
      getTopWinningFilms(5, 1).then(d => setTopFilms(d.topWinningFilms || [])).catch(() => setTopFilms([]));
      getTopGenres().then(d => setGenres(d || { breakdown: [], total: 0 })).catch(() => setGenres({ breakdown: [], total: 0 }));
      getWeeklyWinners(7)
        .then(d => { setWeekly(d.winners || []); setWeeklyDenied(false); })
        .catch((e) => {
          const status = e?.response?.status;
          if (status === 401 || status === 403) setWeeklyDenied(true);
          setWeekly([]);
        });
      getWinnersSummary().then(setWinnerSummary).catch(() => setWinnerSummary({ winners: [] }));
    })();
  }, []);

  useEffect(() => {
    const canPersonal = !!user && (user.role === 'owner' || user.planStatus === 'active');
    const userId = (who === 'me' && canPersonal) ? (user?.id || undefined) : undefined;
    getPollsByUserSeries(userId, granularity).then(d => {
      const pts = (d.series || []).filter(s => s && s.bucket).map(s => ({ label: s.bucket, value: s.cumulative }));
      setSeries(pts);
      setPersonalDenied(false);
    }).catch((e) => {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setPersonalDenied(true);
      setSeries([]);
    });
  }, [who, user, granularity]);

  const heatmapData = useMemo(() => (genres.breakdown || []).filter(g => g && g.genre), [genres]);

  // Movie drawer logic
  const isAdminOrOwner = !!user && (user.role === 'admin' || user.role === 'owner');
  const openMovieDrawer = (movie) => {
    if (!movie) return;
    setSelectedMovie(movie);
    setDrawerOpen(true);
    
    const imdbID = movie.imdbID || null;
    const searchKey = imdbID || `${movie.title}-${movie.year}`;
    
    // Only fetch data if we haven't already loaded it for this movie
    if (omdbFull._for !== searchKey) {
      if (imdbID) {
        // Use IMDB ID if available
        getMovieByImdb(imdbID)
          .then((d) => setOmdbFull({ _for: searchKey, data: d || null }))
          .catch(() => setOmdbFull({ _for: searchKey, data: null }));
      } else {
        // Use title and year search as fallback
        getMovieByTitle(movie.title, movie.year)
          .then((d) => setOmdbFull({ _for: searchKey, data: d || null }))
          .catch(() => setOmdbFull({ _for: searchKey, data: null }));
      }
    }
  };

  const renderWinChips = (title, year, imdbID) => {
    const norm = (s) => (s || '').toString().trim().toLowerCase();
    const wins = (winnerSummary.winners || []).filter(w => (imdbID && w.imdbID && norm(w.imdbID) === norm(imdbID)) || (norm(w.title) === norm(title) && (!year || String(w.year) === String(year))));
    if (!wins.length) return null;
    const w0 = wins[0];
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        <Chip label={`Won ${w0.timesWon || wins.length} ${w0.timesWon===1?'time':'times'}`} size="small" color="primary" />
        {w0.ratingCount ? <Chip label={`Avg Rating ${w0.ratingAvg} (${w0.ratingCount})`} size="small" /> : null}
      </Stack>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Global Metrics</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Top Poll Creators" />
            <CardContent>
              <List dense>
                {topCreators.filter(c => c && c.userId).map((c) => (
                  <ListItem key={c.userId}>
                    <Avatar sx={{ mr: 2 }}>{(c.username || '?').slice(0,1).toUpperCase()}</Avatar>
                    <ListItemText primary={c.username} secondary={`${c.totalPolls} polls`} />
                  </ListItem>
                ))}
                {!topCreators.length && <Typography color="text.secondary">No data</Typography>}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Top 5 Winning Films (by ratings)" />
            <CardContent>
              <List dense>
                {topFilms.filter(f => f && f.movieId).map((f) => (
                  <ListItem key={f.movieId} button onClick={() => openMovieDrawer({ title: f.title, year: f.year, poster: f.poster, imdbID: f.imdbID })}>
                    {f.poster ? <Avatar variant="rounded" src={f.poster} sx={{ mr: 2 }} /> : <Avatar variant="rounded" sx={{ mr: 2 }}>{(f.title||'?')[0]}</Avatar>}
                    <ListItemText primary={`${f.title} (${f.year || ''})`} secondary={`Avg ${f.ratingAvg} (${f.ratingCount}) • Wins ${f.timesWon}`} />
                  </ListItem>
                ))}
                {!topFilms.length && <Typography color="text.secondary">No data</Typography>}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Winning Movies by Genre" subheader="Genre popularity heatmap" />
            <CardContent>
              <GenreHeatmap data={heatmapData} maxItems={20} />
              {heatmapData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                    Hover over genres to see win counts • Color intensity represents popularity
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Weekly Roundup of Winners" />
            <CardContent>
              <List dense>
                {weekly.filter(w => w && w.pollId && w.movieId).map((w) => (
                  <ListItem key={`${w.pollId}-${w.movieId}`} button onClick={() => openMovieDrawer({ title: w.title, year: w.year, poster: w.poster, imdbID: w.imdbID })}>
                    {w.poster ? <Avatar variant="rounded" src={w.poster} sx={{ mr: 2 }} /> : <Avatar variant="rounded" sx={{ mr: 2 }}>{(w.title||'?')[0]}</Avatar>}
                    <ListItemText primary={w.title} secondary={`${w.pollTitle} • ${new Date(w.wonAt).toLocaleString()}`} />
                    <Button size="small" variant="text" onClick={(e) => { e.stopPropagation(); window.location.href = (w.resultsUrl || w.resultsPath); }}>View Results</Button>
                  </ListItem>
                ))}
                {!weekly.length && (
                  <Typography color="text.secondary">
                    {weeklyDenied ? 'Subscribe to Host to view weekly roundups across your clubs.' : 'No winners this week'}
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Polls Created Over Time" subheader="Cumulative by user" />
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <ToggleButtonGroup value={who} exclusive onChange={(_, v) => v && setWho(v)} size="small">
                  <ToggleButton value="me" disabled={!user || !(user.role === 'owner' || user.planStatus === 'active')}>Me</ToggleButton>
                  <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup value={granularity} exclusive onChange={(_, v) => v && setGranularity(v)} size="small">
                  <ToggleButton value="day">Daily</ToggleButton>
                  <ToggleButton value="week">Weekly</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <LineChart points={series} width={720} height={220} />
              {!series.length && (
                <Typography color="text.secondary">
                  {personalDenied && who === 'me' ? 'Subscribe to Host to view your personal series.' : 'No data'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Movie info drawer (limited, similar to Plex) */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 420 }, p: 2, top: { xs: '56px', sm: '64px' }, height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' } } }}>
        {selectedMovie ? (
          <Box>
            <Box sx={{ position: 'sticky', top: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton aria-label="close" onClick={() => setDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="h6" gutterBottom>{selectedMovie.title} {selectedMovie.year ? `(${selectedMovie.year})` : ''}</Typography>
            {(() => {
              const imdbID = selectedMovie.imdbID;
              const d = omdbFull._for === (imdbID || `${selectedMovie.title}-${selectedMovie.year}`) ? omdbFull.data : null;
              const posterUrl = d?.Poster && d.Poster !== 'N/A' ? d.Poster : 
                               (selectedMovie.poster && selectedMovie.poster.startsWith('http') ? selectedMovie.poster : 
                                (selectedMovie.poster ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(selectedMovie.poster)}`) : null));
              
              return posterUrl ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <img src={posterUrl} alt={selectedMovie.title} style={{ maxWidth: '100%', borderRadius: 8 }} />
                </Box>
              ) : null;
            })()}
            {renderWinChips(selectedMovie.title, selectedMovie.year, selectedMovie.imdbID)}
            {(() => {
              const imdbID = selectedMovie.imdbID;
              const searchKey = imdbID || `${selectedMovie.title}-${selectedMovie.year}`;
              const d = omdbFull._for === searchKey ? omdbFull.data : null;
              
              // Show basic movie info even without OMDB data
              if (!d) {
                return (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Basic movie information available. Additional details may be loaded if IMDB data is available.
                    </Typography>
                    {selectedMovie.year && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Year: </strong>{selectedMovie.year}
                      </Typography>
                    )}
                  </Box>
                );
              }
              const toPeopleLinks = (csv) => (csv || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map((name, idx) => (
                  <Button key={`p-${idx}`} size="small" variant="text" sx={{ px: 0.5, minWidth: 0 }}
                    href={`https://www.imdb.com/find/?q=${encodeURIComponent(name)}&s=nm`} target="_blank" rel="noreferrer">
                    {name}
                  </Button>
                ));
              return (
                <Box sx={{ mb: 1 }}>
                  {d.Plot && d.Plot !== 'N/A' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {d.Plot}
                    </Typography>
                  )}
                  {(d.Director && d.Director !== 'N/A') && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Director: </strong>{toPeopleLinks(d.Director)}
                    </Typography>
                  )}
                  {(d.Actors && d.Actors !== 'N/A') && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Cast: </strong>{toPeopleLinks(d.Actors)}
                    </Typography>
                  )}
                  {(d.imdbRating || d.imdbVotes) && (
                    <Chip label={`IMDb ${d.imdbRating || '-'}${d.imdbVotes ? ` (${d.imdbVotes})` : ''}`} size="small" sx={{ mr: 1 }} />
                  )}
                  {isAdminOrOwner && selectedMovie?.poster && selectedMovie.poster !== 'N/A' && !String(selectedMovie.poster).startsWith('http') && (
                    <Chip label="On Plex" color="success" size="small" />
                  )}
                </Box>
              );
            })()}
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">Tip: Click View Results in Weekly Roundup to navigate to poll details.</Typography>
          </Box>
        ) : (
          <Typography>Select a movie to see details</Typography>
        )}
      </Drawer>
    </Box>
  );
};

export default Metrics;
